/**
 * Priority+ nav overflow: on desktop (≥xl=1200px) measures available width,
 * moves items that don't fit into a "More" dropdown. Hover-intent for submenus.
 * Click toggle for both desktop and mobile.
 * Works for both #js-priority-nav (header) and .footer-menu (footer).
 */

const XL = 1200;
const DESKTOP_MIN = 768;

function clearChildren(el) {
    while (el.firstChild) el.removeChild(el.firstChild);
}

function setExpanded(item, open) {
    const label = item.querySelector(':scope > .nav-label');
    if (label) label.setAttribute('aria-expanded', open ? 'true' : 'false');
}

// ── Recursive proxy builder for More dropdown ────────────────────────────

function buildProxyItem(sourceLi) {
    const hasChildren = sourceLi.classList.contains('has-children');
    const proxyLi = document.createElement('li');
    proxyLi.className = hasChildren ? 'nav-item has-children' : 'nav-item';

    if (hasChildren) {
        const sourceLabel = sourceLi.querySelector(':scope > .nav-label');
        const proxySpan = document.createElement('span');
        proxySpan.className = 'nav-link nav-label';
        proxySpan.setAttribute('role', 'button');
        proxySpan.setAttribute('tabindex', '0');
        proxySpan.textContent = sourceLabel ? sourceLabel.textContent.trim() : '';
        proxyLi.appendChild(proxySpan);
    } else {
        const link = sourceLi.querySelector(':scope > .nav-link, :scope > a');
        const proxyA = document.createElement('a');
        proxyA.className = 'nav-link';
        proxyA.href = (link && link.href) ? link.href : '#';
        proxyA.textContent = link ? link.textContent.trim() : '';
        proxyLi.appendChild(proxyA);
    }

    const submenu = sourceLi.querySelector(':scope > .submenu');
    if (submenu) {
        const proxySub = document.createElement('ul');
        proxySub.className = 'priority-nav-submenu';
        submenu.querySelectorAll(':scope > .nav-item').forEach(childLi => {
            proxySub.appendChild(buildProxyItem(childLi));
        });
        proxyLi.appendChild(proxySub);
    }

    return proxyLi;
}

// ── Priority+ overflow ────────────────────────────────────────────────────

function initPriorityNav(navId, opts) {
    const nav = document.getElementById(navId);
    if (!nav) return;

    const moreItem = nav.querySelector('.priority-nav-more');
    const moreDropdown = nav.querySelector('.priority-nav-dropdown');
    if (!moreItem || !moreDropdown) return;

    // moveReal: move actual <li> nodes into the dropdown instead of building proxies.
    // Required for navs whose items contain Bootstrap dropdowns or Vue components
    // (proxies lose data-toggle handlers and Vue instances).
    const moveReal = !!(opts && opts.moveReal);

    const items = Array.from(nav.children).filter(
        li => !li.hasAttribute('data-priority-skip') && !li.classList.contains('priority-nav-more')
    );

    function restoreItems() {
        if (moveReal) {
            // Re-insert real items back into nav in original order, before moreItem
            items.forEach(li => {
                if (li.parentElement !== nav) {
                    nav.insertBefore(li, moreItem);
                }
            });
        } else {
            items.forEach(el => el.classList.remove('d-none'));
            clearChildren(moreDropdown);
        }
    }

    function measure() {
        if (window.innerWidth < DESKTOP_MIN) {
            restoreItems();
            if (!moveReal) clearChildren(moreDropdown);
            moreItem.classList.add('d-none');
            moreItem.classList.remove('is-open');
            moreDropdown.classList.remove('is-open');
            return;
        }

        restoreItems();
        if (!moveReal) clearChildren(moreDropdown);
        moreItem.classList.add('d-none');

        // Measure from the outer navbar container (.container) minus its
        // siblings (logo, etc.) instead of nav.parentElement.offsetWidth.
        // flex-shrink:0 on items inflates the collapse div's width when items
        // overflow, which corrupts the measurement at narrow widths (≈768px).
        const collapse = nav.parentElement;
        const outer = collapse ? collapse.parentElement : null;
        let availableWidth;
        if (outer) {
            const cs = window.getComputedStyle(outer);
            const padLeft = parseFloat(cs.paddingLeft) || 0;
            const padRight = parseFloat(cs.paddingRight) || 0;
            const innerWidth = outer.clientWidth - padLeft - padRight;
            const siblingsWidth = Array.from(outer.children)
                .filter(el => el !== collapse)
                .reduce((sum, el) => sum + el.offsetWidth, 0);
            availableWidth = innerWidth - siblingsWidth;
        } else {
            availableWidth = nav.offsetWidth;
        }

        // Skip items (e.g. phone) are always visible — subtract their width upfront
        const skipWidth = Array.from(nav.children)
            .filter(li => li.hasAttribute('data-priority-skip'))
            .reduce((sum, li) => sum + li.offsetWidth, 0);

        moreItem.classList.remove('d-none');
        const moreWidth = moreItem.offsetWidth;
        moreItem.classList.add('d-none');

        let usedWidth = moreWidth + skipWidth;
        const overflowed = [];

        items.forEach(li => {
            usedWidth += li.offsetWidth;
            if (usedWidth > availableWidth) {
                overflowed.push(li);
            }
        });

        if (overflowed.length === 0) return;

        moreItem.classList.remove('d-none');

        overflowed.forEach(li => {
            if (moveReal) {
                moreDropdown.appendChild(li);
            } else {
                li.classList.add('d-none');
                moreDropdown.appendChild(buildProxyItem(li));
            }
        });
    }

    const moreToggle = moreItem.querySelector('.priority-nav-toggle');
    if (moreToggle) {
        moreToggle.addEventListener('click', (e) => {
            e.stopPropagation();
            const expanded = moreToggle.getAttribute('aria-expanded') === 'true';
            moreToggle.setAttribute('aria-expanded', String(!expanded));
            moreDropdown.classList.toggle('is-open', !expanded);
            moreItem.classList.toggle('is-open', !expanded);
        });
        document.addEventListener('click', (e) => {
            if (moreItem.contains(e.target)) return;
            moreToggle.setAttribute('aria-expanded', 'false');
            moreDropdown.classList.remove('is-open');
            moreItem.classList.remove('is-open');
        });
    }

    let rafPending = false;
    const observer = new ResizeObserver(() => {
        if (rafPending) return;
        rafPending = true;
        requestAnimationFrame(() => {
            rafPending = false;
            measure();
        });
    });

    // Observe the outer navbar container — its width tracks the viewport
    // breakpoint changes; the inner collapse can be polluted by overflowing
    // flex-shrink:0 items.
    const observeTarget = (nav.parentElement && nav.parentElement.parentElement)
        || nav.parentElement
        || nav;
    observer.observe(observeTarget);
    measure();
}

// ── Hover-intent + click toggle for submenus ─────────────────────────────
// Works on any scope containing .has-children > .nav-label

function initSubmenuBehavior(scopeSelector) {
    const OPEN_DELAY = 120;
    const CLOSE_DELAY = 250;

    const scopes = document.querySelectorAll(scopeSelector);
    scopes.forEach(scope => {
        scope.querySelectorAll('.has-children').forEach(item => {
            let openTimer = null;
            let closeTimer = null;

            // Desktop hover-intent
            item.addEventListener('mouseenter', () => {
                clearTimeout(closeTimer);
                if (window.innerWidth < XL) return;
                openTimer = setTimeout(() => {
                    item.classList.add('is-open');
                    setExpanded(item, true);
                }, OPEN_DELAY);
            });

            item.addEventListener('mouseleave', () => {
                clearTimeout(openTimer);
                if (window.innerWidth < XL) return;
                closeTimer = setTimeout(() => {
                    item.classList.remove('is-open');
                    setExpanded(item, false);
                }, CLOSE_DELAY);
            });

            // Click toggle (works for both desktop and mobile)
            const label = item.querySelector(':scope > .nav-label');
            if (label) {
                label.addEventListener('click', (e) => {
                    e.stopPropagation();
                    const wasOpen = item.classList.contains('is-open');

                    // Close sibling submenus at the same level
                    const siblings = item.parentElement
                        ? Array.from(item.parentElement.children).filter(s => s !== item && s.classList && s.classList.contains('has-children'))
                        : [];
                    siblings.forEach(s => {
                        s.classList.remove('is-open');
                        setExpanded(s, false);
                    });

                    if (wasOpen) {
                        item.classList.remove('is-open');
                        setExpanded(item, false);
                    } else {
                        item.classList.add('is-open');
                        setExpanded(item, true);
                    }
                });
            }
        });
    });

    // Close all open submenus on outside click
    document.addEventListener('click', (e) => {
        scopes.forEach(scope => {
            if (scope.contains(e.target)) return;
            scope.querySelectorAll('.has-children.is-open').forEach(item => {
                item.classList.remove('is-open');
                setExpanded(item, false);
            });
        });
    });
}

// ── Init ──────────────────────────────────────────────────────────────────

document.addEventListener('DOMContentLoaded', () => {
    initSubmenuBehavior('#js-priority-nav');
    initSubmenuBehavior('.footer-menu');
});

window.addEventListener('load', () => {
    initPriorityNav('js-priority-nav');
    // Search nav items are Bootstrap dropdowns + Vue components — move real nodes,
    // not proxies, so data-toggle handlers and Vue instances are preserved.
    initPriorityNav('js-priority-nav-search', { moveReal: true });
});
